import React, { useState, useEffect, useContext } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { 
    faRoute, 
    faPlane, 
    faClock, 
    faRulerHorizontal,
    faMapMarkedAlt,
    faMedal,
    faChevronRight,
    faArrowLeft,
    faCheckCircle,
    faCircle,
    faSpinner,
    faPlay,
    faUserPlus
} from "@fortawesome/free-solid-svg-icons";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { AuthContext } from "../../components/auth-context/auth";
import api from "../../api/api";
import "./tours.scss";

export function Tours() {
    const [tours, setTours] = useState([]);
    const [selectedTour, setSelectedTour] = useState(null);
    const [tourProgress, setTourProgress] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { isAuthenticated } = useContext(AuthContext);

    useEffect(() => {
        fetchTours();
    }, []);

    useEffect(() => {
        if (selectedTour && isAuthenticated) {
            fetchEnrollmentProgress(selectedTour.id);
        }
    }, [selectedTour, isAuthenticated]);

    const fetchTours = async () => {
        try {
            setLoading(true);
            const response = await api.get('/tours');
            setTours(response.data);
        } catch (error) {
            console.error('Failed to fetch tours:', error);
            setError(t('tours_fetch_error'));
        } finally {
            setLoading(false);
        }
    };

    const fetchTourDetails = async (tourId) => {
        try {
            const response = await api.get(`/tours/${tourId}`);
            setSelectedTour(response.data);
            
            // Check if user is enrolled and fetch progress
            if (isAuthenticated) {
                fetchEnrollmentProgress(tourId);
            }
        } catch (error) {
            console.error('Failed to fetch tour details:', error);
        }
    };

    const fetchEnrollmentProgress = async (tourId) => {
        try {
            const response = await api.get(`/tour-enrollment/progress/${tourId}`);
            if (response.data.enrolled) {
                setTourProgress(response.data);
            } else {
                setTourProgress(null);
            }
        } catch (error) {
            console.error('Failed to fetch enrollment progress:', error);
            setTourProgress(null);
        }
    };

    const handleTourClick = (tour) => {
        fetchTourDetails(tour.id);
    };

    const handleBackClick = () => {
        setSelectedTour(null);
        setTourProgress(null);
    };

    const formatTime = (minutes) => {
        if (!minutes) return '--';
        const hours = Math.floor(minutes / 60);
        const mins = minutes % 60;
        return `${hours}h ${mins}min`;
    };

    const formatDate = (date) => {
        if (!date) return '--';
        return new Date(date).toLocaleDateString();
    };

    const handleEnrollInTour = async () => {
        try {
            const response = await api.post('/tour-enrollment/enroll', {
                tourId: selectedTour.id
            });
            
            if (response.data) {
                // Refresh enrollment progress
                fetchEnrollmentProgress(selectedTour.id);
                alert(t('enrolled_successfully'));
            }
        } catch (error) {
            console.error('Failed to enroll in tour:', error);
            alert(error.response?.data?.error || t('error_enrolling'));
        }
    };

    const handleStartLeg = (leg) => {
        // Navigate to manager with the tour leg info
        navigate('/manager', {
            state: {
                mode: 'tour',
                departure: leg.airportDepartureIcao,
                arrival: leg.airportArrivalIcao,
                tourName: selectedTour.name,
                legOrder: leg.order
            }
        });
    };

    const isLegCompleted = (legOrder) => {
        return tourProgress?.enrollment?.completedLegs >= legOrder;
    };

    const getNextLeg = () => {
        if (!selectedTour || !tourProgress?.enrollment) return null;
        const nextOrder = tourProgress.enrollment.currentLegOrder;
        return selectedTour.legs.find(leg => leg.order === nextOrder);
    };

    if (loading) {
        return (
            <div className="tours-container">
                <div className="loading">
                    <FontAwesomeIcon icon={faSpinner} spin />
                    <p>{t('loading')}</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="tours-container">
                <div className="error-message">{error}</div>
            </div>
        );
    }

    if (selectedTour) {
        const nextLeg = getNextLeg();
        
        return (
            <div className="tours-container">
                <div className="tour-detail">
                    <button className="back-button" onClick={handleBackClick}>
                        <FontAwesomeIcon icon={faArrowLeft} />
                        {t('back_to_tours')}
                    </button>

                    {selectedTour.bannerImage && (
                        <div className="tour-banner">
                            <img src={selectedTour.bannerImage} alt={selectedTour.name} />
                        </div>
                    )}

                    <div className="tour-header">
                        <h2>{selectedTour.name}</h2>
                        {selectedTour.medal && (
                            <div className="tour-medal">
                                <img src={selectedTour.medal.img} alt={selectedTour.medal.text} />
                                <span>{selectedTour.medal.text}</span>
                            </div>
                        )}
                    </div>

                    <div className="tour-info">
                        <div className="info-card">
                            <FontAwesomeIcon icon={faMapMarkedAlt} />
                            <span>{selectedTour.legs?.length || 0} {t('legs')}</span>
                        </div>
                        <div className="info-card">
                            <FontAwesomeIcon icon={faRulerHorizontal} />
                            <span>{selectedTour.totalDistance || '--'} NM</span>
                        </div>
                        <div className="info-card">
                            <FontAwesomeIcon icon={faClock} />
                            <span>{formatTime(selectedTour.totalTime)}</span>
                        </div>
                        <div className="info-card">
                            <FontAwesomeIcon icon={faPlane} />
                            <span>{selectedTour.recommendedAircrafts ? 
                                JSON.parse(selectedTour.recommendedAircrafts).join(', ') : 
                                t('any_aircraft')}</span>
                        </div>
                    </div>

                    <div className="tour-description">
                        <p>{selectedTour.description}</p>
                    </div>

                    {isAuthenticated && !tourProgress && (
                        <div className="enroll-section">
                            <button 
                                className="enroll-button"
                                onClick={handleEnrollInTour}
                            >
                                <FontAwesomeIcon icon={faUserPlus} />
                                {t('enroll_in_tour')}
                            </button>
                        </div>
                    )}

                    {tourProgress && (
                        <div className="tour-progress-bar">
                            <div className="progress-header">
                                <h3>{t('your_progress')}</h3>
                                <span className="progress-percentage">{tourProgress.percentage}%</span>
                            </div>
                            <div className="progress-bar">
                                <div 
                                    className="progress-fill" 
                                    style={{ width: `${tourProgress.percentage}%` }}
                                />
                            </div>
                            <p className="progress-text">
                                {tourProgress.completedLegs} {t('of')} {tourProgress.totalLegs} {t('legs_completed')}
                            </p>
                        </div>
                    )}

                    <div className="tour-legs">
                        <h3>{t('tour_legs')}</h3>
                        <div className="legs-list">
                            {selectedTour.legs?.map((leg, index) => {
                                const isCompleted = isLegCompleted(leg.order);
                                const isNext = nextLeg?.id === leg.id;
                                const isPending = tourProgress?.pendingLegIds?.includes(leg.id);
                                
                                return (
                                    <div 
                                        key={leg.id} 
                                        className={`leg-card ${isCompleted ? 'completed' : ''} ${isNext ? 'next' : ''} ${isPending ? 'pending' : ''}`}
                                    >
                                        <div className="leg-number">
                                            {isCompleted ? (
                                                <FontAwesomeIcon icon={faCheckCircle} className="completed-icon" />
                                            ) : isPending ? (
                                                <FontAwesomeIcon icon={faClock} className="pending-icon" />
                                            ) : (
                                                <span className="number">{leg.order}</span>
                                            )}
                                        </div>
                                        
                                        <div className="leg-info">
                                            <div className="leg-route">
                                                <span className="airport-code">{leg.airportDepartureIcao}</span>
                                                <FontAwesomeIcon icon={faChevronRight} className="arrow" />
                                                <span className="airport-code">{leg.airportArrivalIcao}</span>
                                            </div>
                                            <div className="leg-airports">
                                                <span className="airport-name">
                                                    {leg.airportDeparture?.name || leg.airportDepartureIcao}
                                                </span>
                                                <span className="separator">→</span>
                                                <span className="airport-name">
                                                    {leg.airportArrival?.name || leg.airportArrivalIcao}
                                                </span>
                                            </div>
                                            <div className="leg-stats">
                                                <span>
                                                    <FontAwesomeIcon icon={faRulerHorizontal} />
                                                    {leg.distance || '--'} NM
                                                </span>
                                                <span>
                                                    <FontAwesomeIcon icon={faClock} />
                                                    {formatTime(leg.estimatedTime)}
                                                </span>
                                            </div>
                                        </div>

                                        <div className="leg-action">
                                            {!isCompleted && !isPending && (
                                                <button 
                                                    className={`fly-button ${isNext ? 'primary' : ''}`}
                                                    onClick={() => handleStartLeg(leg)}
                                                    disabled={!isNext && tourProgress}
                                                >
                                                    <FontAwesomeIcon icon={faPlay} />
                                                    {isNext ? t('fly_next') : t('fly')}
                                                </button>
                                            )}
                                            {isPending && (
                                                <span className="pending-text">{t('pending_validation') || 'Pendiente de validar'}</span>
                                            )}
                                            {isCompleted && (
                                                <span className="completed-text">{t('completed')}</span>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="tours-container">
                <div className="tours-header">
                    <h2>
                        <FontAwesomeIcon icon={faRoute} /> {t('tours')}
                    </h2>
                </div>
                
                <div className="tours-content">
                    <div className="tours-intro">
                        <p>{t('tours_intro')}</p>
                    </div>

                    {tours.length === 0 ? (
                        <div className="no-tours">
                            <p>{t('no_tours_available')}</p>
                        </div>
                    ) : (
                        <div className="tours-grid">
                    {tours.map((tour) => (
                        <div 
                            className="tour-card" 
                            key={tour.id}
                            onClick={() => handleTourClick(tour)}
                        >
                            <div className="tour-image">
                                <img src={tour.img} alt={tour.name} />
                                {tour.medal && (
                                    <div className="medal-badge">
                                        <img src={tour.medal.img} alt={tour.medal.text} />
                                    </div>
                                )}
                            </div>
                            
                            <div className="tour-content">
                                <h3>{tour.name}</h3>
                                <p className="tour-short-description">
                                    {tour.shortDescription}
                                </p>
                                
                                <div className="tour-stats">
                                    <span>
                                        <FontAwesomeIcon icon={faMapMarkedAlt} />
                                        {tour.totalLegs} {t('legs')}
                                    </span>
                                    <span>
                                        <FontAwesomeIcon icon={faRulerHorizontal} />
                                        {tour.totalDistance || 0} NM
                                    </span>
                                    <span>
                                        <FontAwesomeIcon icon={faClock} />
                                        {formatTime(tour.totalTime)}
                                    </span>
                                </div>

                                <div className="tour-dates">
                                    <span className="date-label">{t('available_until')}:</span>
                                    <span className="date-value">{formatDate(tour.close_day)}</span>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
                    )}
                </div>
            </div>
    );
}